import { ref, onUnmounted } from "vue";
import { useToast } from "vue-toast-notification";
import { useUserStore } from "@/stores/user";
import { getCode, updatePhone, updateUsername } from "@/api/login";

const PHONE_PATTERN = /^1[3-9]\d{9}$/;

export function useProfileEdit() {
  const $toast = useToast();
  const userStore = useUserStore();

  const showNicknameDialog = ref(false);
  const showPhoneDialog = ref(false);
  const nicknameInput = ref("");
  const newPhone = ref("");
  const phoneCode = ref("");
  const submitting = ref(false);
  const countdown = ref(0);

  let countdownTimer = null;

  const clearCountdown = () => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
    countdown.value = 0;
  };

  const openNicknameDialog = () => {
    nicknameInput.value = userStore.userInfo?.username || "";
    showNicknameDialog.value = true;
  };

  const resetNicknameDialog = () => {
    nicknameInput.value = "";
  };

  const openPhoneDialog = () => {
    newPhone.value = "";
    phoneCode.value = "";
    showPhoneDialog.value = true;
  };

  const resetPhoneDialog = () => {
    newPhone.value = "";
    phoneCode.value = "";
    clearCountdown();
  };

  const submitNickname = async () => {
    const username = nicknameInput.value.trim();
    if (!username) {
      $toast.error("请输入昵称", { position: "top" });
      return;
    }
    if (username === userStore.userInfo?.username) {
      showNicknameDialog.value = false;
      return;
    }

    submitting.value = true;
    try {
      const res = await updateUsername({ username });
      if (res.code === 200) {
        $toast.success("昵称修改成功", { position: "top" });
        showNicknameDialog.value = false;
        await userStore.getUser();
      } else {
        $toast.error(res.message || "昵称修改失败", { position: "top" });
      }
    } catch {
      $toast.error("昵称修改失败", { position: "top" });
    } finally {
      submitting.value = false;
    }
  };

  const sendPhoneCode = async () => {
    const phone = newPhone.value.trim();
    if (!PHONE_PATTERN.test(phone)) {
      $toast.error("请输入正确的手机号", { position: "top" });
      return;
    }
    if (phone === userStore.userInfo?.phone) {
      $toast.error("新手机号不能与当前号码相同", { position: "top" });
      return;
    }
    if (countdown.value > 0) return;

    try {
      const res = await getCode({ phone });
      if (res.code === 200) {
        $toast.success("验证码已发送", { position: "top" });
        countdown.value = 60;
        countdownTimer = setInterval(() => {
          if (countdown.value > 0) {
            countdown.value -= 1;
          } else {
            clearCountdown();
          }
        }, 1000);
      } else {
        $toast.error(res.message || "验证码发送失败", { position: "top" });
      }
    } catch {
      $toast.error("验证码发送失败", { position: "top" });
    }
  };

  const submitPhone = async () => {
    const phone = newPhone.value.trim();
    const code = phoneCode.value.trim();

    if (!PHONE_PATTERN.test(phone)) {
      $toast.error("请输入正确的手机号", { position: "top" });
      return;
    }
    if (!code) {
      $toast.error("请输入验证码", { position: "top" });
      return;
    }

    submitting.value = true;
    try {
      const res = await updatePhone({ newPhone: phone, code });
      if (res.code === 200) {
        $toast.success("手机号更换成功", { position: "top" });
        showPhoneDialog.value = false;
        await userStore.getUser();
      } else {
        $toast.error(res.message || "手机号更换失败", { position: "top" });
      }
    } catch {
      $toast.error("手机号更换失败", { position: "top" });
    } finally {
      submitting.value = false;
    }
  };

  onUnmounted(clearCountdown);

  return {
    showNicknameDialog,
    showPhoneDialog,
    nicknameInput,
    newPhone,
    phoneCode,
    submitting,
    countdown,
    openNicknameDialog,
    openPhoneDialog,
    resetNicknameDialog,
    resetPhoneDialog,
    submitNickname,
    sendPhoneCode,
    submitPhone,
  };
}
